/** The decoration scatter: what stops terrain reading as a repeating stamp.
 *
 * Placement is a pure function of the cell coordinate, so it is stable across
 * frames, identical for every player, and needs no state and no RNG stream -
 * which matters, because a decal that moved when the camera moved, or differed
 * between two clients, would be a visible desync even though it decides nothing.
 *
 * Only *flat* clutter belongs here. Anything with height (a tree, a boulder) is
 * a map object with a footprint and a shadow, because it has to depth-sort and
 * it usually blocks movement.
 */

import type { TerrainId } from "./dualgrid.js";

/** 32-bit integer mix. Deterministic, no floats, no ambient state. */
export function hash32(x: number, y: number, salt: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(salt | 0, 0x9e3779b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

const SALT_PRESENT = 0x51ed270b;
const SALT_KIND = 0x2545f491;
const SALT_OFFSET = 0x9e3779b9;
const SALT_TONE = 0x7feb352d;

export interface DecalKind {
  /** Atlas key, already namespaced (`obj.obs_*`). */
  readonly key: string;
  /** Relative weight within the terrain's table. */
  readonly weight: number;
}

export interface DecalRule {
  /** Chance a cell carries a decal, in basis points out of 10000. */
  readonly densityBp: number;
  readonly kinds: readonly DecalKind[];
}

const k = (key: string, weight = 1): DecalKind => ({ key: `obj.${key}`, weight });

/**
 * Density is deliberately uneven per terrain: grass and swamp are busy, sand
 * and snow are sparse, rock is nearly bare. A uniform density reads as noise
 * rather than as landscape.
 *
 * The common entries are all one tile or smaller. The 2x2 sprites - a dune, a
 * scree field, a bog pool - carry weights around 0.2 on purpose: at a common
 * weight they tile the ground with big pale pancakes and the layer stops
 * reading as clutter and starts reading as a mistake.
 */
export const DECAL_RULES: Readonly<Record<TerrainId, DecalRule>> = Object.freeze({
  water: { densityBp: 650, kinds: [k("obs_lily_pads", 2), k("obs_coral", 2), k("obs_ice_floe", 0.15)] },
  sand: {
    densityBp: 1500,
    kinds: [k("obs_pebbles", 3), k("obs_desert_shrub", 2), k("obs_tumbleweed", 1.5),
      k("obs_desert_rock", 1.5), k("obs_desert_bones"), k("obs_camel_bones", 0.5),
      k("obs_oasis_reeds", 0.5), k("obs_sand_ripple", 0.25), k("obs_salt_crust", 0.15)],
  },
  dirt: {
    densityBp: 1800,
    kinds: [k("obs_pebbles", 3), k("obs_tall_grass", 2.5), k("obs_puddle", 1.5),
      k("obs_molehill", 1.5), k("obs_rabbit_burrow"), k("obs_rocks"), k("obs_stump", 0.8),
      k("obs_dry_well", 0.3), k("obs_dry_riverbed", 0.15)],
  },
  grass: {
    densityBp: 2200,
    kinds: [k("obs_tall_grass", 4), k("obs_flowers", 3), k("obs_clover_patch", 2),
      k("obs_bush", 2), k("obs_forest_ferns", 1.5), k("obs_hazel_bush"), k("obs_wildrose"),
      k("obs_mushroom", 0.8), k("obs_molehill", 0.8), k("obs_flower_ring", 0.4),
      k("obs_stone_bench", 0.15)],
  },
  snow: {
    densityBp: 1300,
    kinds: [k("obs_frost_bush", 2.5), k("obs_snow_boulder", 2), k("obs_snow_stump", 1.5),
      k("obs_iceblock"), k("obs_bones", 0.4), k("obs_snow_drift", 0.3),
      k("obs_snow_hummock", 0.25), k("obs_glacier_crack", 0.12)],
  },
  swamp: {
    densityBp: 2500,
    kinds: [k("obs_marsh_grass", 4), k("obs_dead_reed", 2.5), k("obs_bog_bubbles", 2),
      k("obs_lily_pads", 1.5), k("obs_peat_mound"), k("obs_sunken_stump"),
      k("obs_leech_pool", 0.6), k("obs_bog_pool", 0.25)],
  },
  rough: {
    densityBp: 2000,
    kinds: [k("obs_rocks", 3), k("obs_pebbles", 2.5), k("obs_deadbush", 2),
      k("obs_moss_rock", 1.5), k("obs_nettle_patch"), k("obs_rubble_pile"),
      k("obs_desert_rock", 0.8), k("obs_scree", 0.25), k("obs_boulder", 0.1)],
  },
  cavern: {
    densityBp: 1900,
    kinds: [k("obs_glow_moss", 3), k("obs_pebbles", 2), k("obs_crystal_vein", 1.5),
      k("obs_shelf_fungus", 1.5), k("obs_bat_roost"), k("obs_bone_pile", 0.6),
      k("obs_cave_pool", 0.25)],
  },
  lava: {
    densityBp: 1700,
    kinds: [k("obs_lavarock", 3), k("obs_ember_pile", 2), k("obs_ash_mound", 2),
      k("obs_sulfur_crust", 1.2), k("obs_basalt_column", 0.8), k("obs_ash_drift", 0.25),
      k("obs_lavacrack", 0.15)],
  },
  rock: {
    densityBp: 350,
    kinds: [k("obs_rubble_pile", 2), k("obs_pebbles", 2), k("obs_rocks"), k("obs_scree", 0.15)],
  },
  highlands: {
    densityBp: 1900,
    kinds: [k("obs_heather_patch", 3), k("obs_tall_grass", 2.5), k("obs_pebbles", 2),
      k("obs_clover_patch", 1.5), k("obs_molehill"), k("obs_forest_ferns", 0.8),
      k("obs_rocks", 0.6)],
  },
  wasteland: {
    densityBp: 1600,
    kinds: [k("obs_tar_seep", 2.5), k("obs_pebbles", 2.5), k("obs_rubble_pile", 2),
      k("obs_bones", 1.5), k("obs_ash_drift"), k("obs_sulfur_crust", 0.6),
      k("obs_dry_riverbed", 0.15)],
  },
});

/**
 * The decals that are marks ON the ground rather than things standing on it.
 *
 * They get no shadow. The trimmed box cannot tell you which is which - this art
 * is drawn in a three-quarter projection, so a puddle packs to 64x49 and a bush
 * to 64x61, and any aspect-ratio rule that catches the puddle also catches the
 * clover. Height is a property of the SUBJECT, so it is listed here beside the
 * placement tables that already name every kind, rather than guessed at in the
 * layer that draws them.
 *
 * The test of membership is one question: if you poured water on it, would the
 * water sit on top? A tar seep, a lily pad and a lava crack are ground. A
 * molehill, a stump and a mushroom are not.
 */
export const FLAT_DECALS: ReadonlySet<string> = new Set([
  "obj.obs_lily_pads", "obj.obs_ice_floe", "obj.obs_coral",
  "obj.obs_sand_ripple", "obj.obs_salt_crust",
  "obj.obs_puddle", "obj.obs_dry_riverbed",
  "obj.obs_snow_drift", "obj.obs_glacier_crack",
  "obj.obs_bog_bubbles", "obj.obs_leech_pool", "obj.obs_bog_pool",
  "obj.obs_glow_moss", "obj.obs_cave_pool",
  "obj.obs_lavacrack", "obj.obs_ash_drift", "obj.obs_sulfur_crust",
  "obj.obs_tar_seep", "obj.obs_scree",
]);

export interface DecalPlacement {
  readonly key: string;
  /** World-pixel position of the decal's ground line. */
  readonly groundX: number;
  readonly groundY: number;
  /** 0..1; a little variety in weight keeps the layer from reading as stickers. */
  readonly alpha: number;
}

/**
 * The decal on a cell, or `null`.
 *
 * Offsets stay inside the middle half of the tile so a decal never crosses a
 * terrain boundary it was not chosen for.
 */
export function decalAt(
  x: number, y: number, terrain: TerrainId, tile: number,
  rules: Readonly<Record<TerrainId, DecalRule>> = DECAL_RULES,
): DecalPlacement | null {
  const rule = rules[terrain];
  if (rule === undefined || rule.kinds.length === 0) return null;
  if (hash32(x, y, SALT_PRESENT) % 10000 >= rule.densityBp) return null;

  let total = 0;
  for (const kind of rule.kinds) total += kind.weight;
  // 1e6 buckets: enough resolution that a 0.4 weight is not rounded away.
  let pick = (hash32(x, y, SALT_KIND) % 1000000) / 1000000 * total;
  let chosen = rule.kinds[0]!;
  for (const kind of rule.kinds) {
    pick -= kind.weight;
    if (pick < 0) { chosen = kind; break; }
  }

  const off = hash32(x, y, SALT_OFFSET);
  const quarter = tile / 4;
  const dx = ((off & 0xff) / 255 - 0.5) * quarter;
  const dy = (((off >>> 8) & 0xff) / 255 - 0.5) * quarter;
  const tone = (hash32(x, y, SALT_TONE) & 0x3f) / 63;

  return {
    key: chosen.key,
    groundX: x * tile + tile / 2 + dx,
    groundY: (y + 1) * tile + dy,
    alpha: 0.72 + tone * 0.28,
  };
}
